import { useState, useCallback, useEffect, useRef } from 'react';
import { apiRequest } from '../api/client';
import {
  EMPTY_ASSEMBLY,
  EMPTY_NEWSPAPER_ADDRESS,
  EMPTY_NEWSPAPER_REQUEST,
  EMPTY_REPORT
} from '../constants/forms';
import { statusLabel } from '../utils/statusUtils';

export function useAssemblies(token, notify) {
  // ── List
  const [assemblies, setAssemblies] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [operationalMetrics, setOperationalMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsDays, setMetricsDays] = useState(14);
  const [newspaperAddresses, setNewspaperAddresses] = useState([]);
  const [newspaperRequests, setNewspaperRequests] = useState([]);
  const [newspaperLoading, setNewspaperLoading] = useState(false);
  const [newspaperAddressSaving, setNewspaperAddressSaving] = useState(false);
  const [newspaperRequestSaving, setNewspaperRequestSaving] = useState(false);
  const [newspaperAddressForm, setNewspaperAddressForm] = useState(EMPTY_NEWSPAPER_ADDRESS);
  const [newspaperRequestForm, setNewspaperRequestForm] = useState(EMPTY_NEWSPAPER_REQUEST);
  const [newspaperStates, setNewspaperStates] = useState([]);
  const [newspaperMunicipalities, setNewspaperMunicipalities] = useState([]);
  const [newspaperFederalDistricts, setNewspaperFederalDistricts] = useState([]);
  const [newspaperPostalCodes, setNewspaperPostalCodes] = useState([]);
  const [newspaperColonias, setNewspaperColonias] = useState([]);
  const [newspaperCities, setNewspaperCities] = useState([]);
  const [newspaperCatalogLoading, setNewspaperCatalogLoading] = useState(false);
  const [newspaperMunicipalitiesLoading, setNewspaperMunicipalitiesLoading] = useState(false);

  // ── Create modal
  const [showModal,   setShowModal]   = useState(false);
  const [modalForm,   setModalForm]   = useState(EMPTY_ASSEMBLY);
  const [modalSaving, setModalSaving] = useState(false);

  // ── Detail drawer
  const [drawerAssembly,  setDrawerAssembly]  = useState(null);
  const [drawerReport,    setDrawerReport]    = useState(null);
  const [drawerEvidences, setDrawerEvidences] = useState([]);
  const [drawerTab,       setDrawerTab]       = useState('report');
  const [reportForm,      setReportForm]      = useState(EMPTY_REPORT);
  const [reportSaving,    setReportSaving]    = useState(false);

  // ── User history
  const [myAssemblyHistory, setMyAssemblyHistory] = useState([]);
  const [myAssemblyHistoryLoading, setMyAssemblyHistoryLoading] = useState(false);

  // ── Upload
  const [uploadType,   setUploadType]   = useState('photo');
  const [uploadFiles,  setUploadFiles]  = useState([]);
  const [uploadSaving, setUploadSaving] = useState(false);
  const fileRef = useRef(null);

  // ─────────────────────────────────────────────
  const loadAssemblies = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await apiRequest('/assemblies', { method: 'GET' }, token);
      setAssemblies(data);
    } catch (err) {
      notify('error', err.message);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMyAssemblyHistory = useCallback(async () => {
    if (!token) {
      setMyAssemblyHistory([]);
      return;
    }

    setMyAssemblyHistoryLoading(true);
    try {
      const list = Array.isArray(assemblies) ? assemblies : [];
      if (!list.length) {
        setMyAssemblyHistory([]);
        return;
      }

      const snapshots = await Promise.all(list.map(async (assembly) => {
        try {
          const report = await apiRequest(`/assemblies/${assembly.id}/report`, { method: 'GET' }, token);
          if (!report) {
            return { assembly, report: null, evidences: [] };
          }

          const evidences = await apiRequest(`/reports/${report.id}/evidences`, { method: 'GET' }, token);
          return {
            assembly,
            report,
            evidences: Array.isArray(evidences) ? evidences : []
          };
        } catch (_) {
          return { assembly, report: null, evidences: [] };
        }
      }));

      setMyAssemblyHistory(snapshots);
    } catch (err) {
      setMyAssemblyHistory([]);
      notify('error', err.message || 'No se pudo cargar el historial de asambleas.');
    } finally {
      setMyAssemblyHistoryLoading(false);
    }
  }, [assemblies, token, notify]);

  useEffect(() => {
    if (!token) {
      setMyAssemblyHistory([]);
      return;
    }

    if (!assemblies.length) {
      setMyAssemblyHistory([]);
      return;
    }

    loadMyAssemblyHistory();
  }, [assemblies, token, loadMyAssemblyHistory]);

  const loadOperationalMetrics = useCallback(async (days = 14) => {
    if (!token) return;
    setMetricsLoading(true);
    try {
      const safeDays = Math.max(1, Math.min(90, Number(days) || 14));
      const metrics = await apiRequest(`/dashboard/operational-metrics?days=${safeDays}`, { method: 'GET' }, token);
      setOperationalMetrics(metrics || null);
    } catch (err) {
      notify('error', err.message || 'No se pudieron cargar las metricas operativas.');
    } finally {
      setMetricsLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshOperationalMetrics = useCallback(async (days) => {
    const safeDays = Math.max(1, Math.min(90, Number(days) || metricsDays));
    setMetricsDays(safeDays);
    await loadOperationalMetrics(safeDays);
  }, [metricsDays, loadOperationalMetrics]);

  const loadNewspaperMunicipalities = useCallback(async (stateIdRaw) => {
    const stateId = Number(stateIdRaw);
    if (!Number.isInteger(stateId) || stateId < 1) {
      setNewspaperMunicipalities([]);
      return;
    }

    setNewspaperMunicipalitiesLoading(true);
    try {
      const data = await apiRequest(
        `/catalog/municipalities?entidad=${encodeURIComponent(String(stateId))}`,
        { method: 'GET' },
        token
      );
      setNewspaperMunicipalities(Array.isArray(data) ? data : []);
    } catch (err) {
      setNewspaperMunicipalities([]);
      notify('error', err.message || 'No se pudo cargar el catalogo de municipios.');
    } finally {
      setNewspaperMunicipalitiesLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNewspaperFederalDistricts = useCallback(async (stateIdRaw) => {
    const stateId = Number(stateIdRaw);
    if (!Number.isInteger(stateId) || stateId < 1) {
      setNewspaperFederalDistricts([]);
      return;
    }

    try {
      const data = await apiRequest(
        `/catalog/federal-districts?entidad=${encodeURIComponent(String(stateId))}`,
        { method: 'GET' },
        token
      );
      setNewspaperFederalDistricts(Array.isArray(data) ? data : []);
    } catch (err) {
      setNewspaperFederalDistricts([]);
      notify('error', err.message || 'No se pudo cargar el catalogo de distritos.');
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNewspaperAddressCatalogs = useCallback(async ({ state, municipality, postalCode = '' }) => {
    const safeState = String(state || '').trim();
    const safeMunicipality = String(municipality || '').trim();
    const safePostalCode = String(postalCode || '').trim();

    if (!safeState || !safeMunicipality) {
      setNewspaperPostalCodes([]);
      setNewspaperColonias([]);
      setNewspaperCities([]);
      return;
    }

    const qpBase = new URLSearchParams({ state: safeState, municipality: safeMunicipality });
    const qpWithPostal = new URLSearchParams({
      state: safeState,
      municipality: safeMunicipality,
      ...(safePostalCode ? { postalCode: safePostalCode } : {})
    });

    try {
      const [postalCodes, colonias, cities] = await Promise.all([
        apiRequest(`/catalog/newspaper-postal-codes?${qpBase.toString()}`, { method: 'GET' }, token),
        apiRequest(`/catalog/newspaper-colonias?${qpWithPostal.toString()}`, { method: 'GET' }, token),
        apiRequest(`/catalog/newspaper-cities?${qpWithPostal.toString()}`, { method: 'GET' }, token)
      ]);

      setNewspaperPostalCodes(Array.isArray(postalCodes) ? postalCodes : []);
      setNewspaperColonias(Array.isArray(colonias) ? colonias : []);
      setNewspaperCities(Array.isArray(cities) ? cities : []);
    } catch (err) {
      setNewspaperPostalCodes([]);
      setNewspaperColonias([]);
      setNewspaperCities([]);
      notify('error', err.message || 'No se pudieron cargar los catalogos de domicilio.');
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleNewspaperStateChange(stateIdValue) {
    const selectedState = newspaperStates.find((item) => String(item.id) === String(stateIdValue));
    setNewspaperAddressForm((prev) => ({
      ...prev,
      stateId: String(stateIdValue || ''),
      state: selectedState ? selectedState.name : '',
      federalDistrict: '',
      municipalityId: '',
      municipality: '',
      postalCode: '',
      colonia: '',
      city: ''
    }));
    setNewspaperFederalDistricts([]);
    setNewspaperPostalCodes([]);
    setNewspaperColonias([]);
    setNewspaperCities([]);
  }

  function handleNewspaperMunicipalityChange(municipalityIdValue) {
    const selectedMunicipality = newspaperMunicipalities.find(
      (item) => String(item.id) === String(municipalityIdValue)
    );

    setNewspaperAddressForm((prev) => ({
      ...prev,
      municipalityId: String(municipalityIdValue || ''),
      municipality: selectedMunicipality ? selectedMunicipality.name : '',
      postalCode: '',
      colonia: '',
      city: ''
    }));
  }

  function handleNewspaperPostalCodeChange(postalCodeRaw) {
    const nextValue = String(postalCodeRaw || '').replace(/\D/g, '').slice(0, 5);
    setNewspaperAddressForm((prev) => ({ ...prev, postalCode: nextValue, colonia: '', city: '' }));
  }

  const loadNewspaperData = useCallback(async () => {
    if (!token) return;
    setNewspaperLoading(true);
    setNewspaperCatalogLoading(true);
    try {
      const [addresses, requests, states] = await Promise.all([
        apiRequest('/newspaper/addresses', { method: 'GET' }, token),
        apiRequest('/newspaper/requests', { method: 'GET' }, token),
        apiRequest('/catalog/states', { method: 'GET' }, token)
      ]);
      const safeAddresses = Array.isArray(addresses) ? addresses : [];
      const safeRequests = Array.isArray(requests) ? requests : [];
      const safeStates = Array.isArray(states) ? states : [];
      setNewspaperAddresses(safeAddresses);
      setNewspaperRequests(safeRequests);
      setNewspaperStates(safeStates);

      setNewspaperRequestForm((prev) => {
        if (String(prev.addressId || '').trim()) return prev;
        const defaultAddressId = safeAddresses.length ? String(safeAddresses[0].id) : '';
        return { ...prev, addressId: defaultAddressId };
      });

    } catch (err) {
      notify('error', err.message || 'No se pudo cargar el modulo de periodico.');
    } finally {
      setNewspaperLoading(false);
      setNewspaperCatalogLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedStateId = String(newspaperAddressForm.stateId || '').trim();
  const selectedState = String(newspaperAddressForm.state || '').trim();
  const selectedMunicipality = String(newspaperAddressForm.municipality || '').trim();
  const selectedPostalCode = String(newspaperAddressForm.postalCode || '').trim();

  // Mantiene sincronizado el catálogo dependiente de municipios con el estado elegido en el formulario.
  useEffect(() => {
    loadNewspaperMunicipalities(selectedStateId);
    loadNewspaperFederalDistricts(selectedStateId);
  }, [selectedStateId, loadNewspaperMunicipalities, loadNewspaperFederalDistricts]);

  useEffect(() => {
    if (selectedPostalCode && selectedPostalCode.length > 0 && selectedPostalCode.length < 5) {
      return;
    }
    const postalForFilter = selectedPostalCode.length === 5 ? selectedPostalCode : '';
    loadNewspaperAddressCatalogs({
      state: selectedState,
      municipality: selectedMunicipality,
      postalCode: postalForFilter
    });
  }, [selectedState, selectedMunicipality, selectedPostalCode, loadNewspaperAddressCatalogs]);


  async function createNewspaperAddress(e) {
    e.preventDefault();
    setNewspaperAddressSaving(true);
    try {
      const created = await apiRequest('/newspaper/addresses', {
        method: 'POST',
        body: JSON.stringify(newspaperAddressForm)
      }, token);

      setNewspaperAddresses((prev) => [created, ...prev]);
      setNewspaperAddressForm(EMPTY_NEWSPAPER_ADDRESS);
      setNewspaperRequestForm((prev) => ({ ...prev, addressId: String(created.id) }));
      notify('success', 'Domicilio registrado para recibir periodico.');
    } catch (err) {
      notify('error', err.message || 'No se pudo guardar el domicilio.');
    } finally {
      setNewspaperAddressSaving(false);
    }
  }

  async function createNewspaperRequest(e) {
    e.preventDefault();
    setNewspaperRequestSaving(true);
    try {
      const payload = {
        addressId: Number(newspaperRequestForm.addressId),
        quantity: Number(newspaperRequestForm.quantity)
      };

      const created = await apiRequest('/newspaper/requests', {
        method: 'POST',
        body: JSON.stringify(payload)
      }, token);

      setNewspaperRequests((prev) => [created, ...prev]);
      setNewspaperRequestForm((prev) => ({ ...prev, quantity: 1 }));
      notify('success', 'Solicitud de periodico registrada.');
    } catch (err) {
      notify('error', err.message || 'No se pudo crear la solicitud de periodico.');
    } finally {
      setNewspaperRequestSaving(false);
    }
  }

  function reset() {
    setAssemblies([]);
    setMyAssemblyHistory([]);
    setOperationalMetrics(null);
    setNewspaperAddresses([]);
    setNewspaperRequests([]);
    setNewspaperAddressForm(EMPTY_NEWSPAPER_ADDRESS);
    setNewspaperRequestForm(EMPTY_NEWSPAPER_REQUEST);
    closeDrawer();
  }

  // ── Drawer
  async function openDrawer(assembly) {
    setDrawerAssembly(assembly);
    setDrawerTab('report');
    setDrawerReport(null);
    setDrawerEvidences([]);
    try {
      const rep = await apiRequest(`/assemblies/${assembly.id}/report`, { method: 'GET' }, token);
      setDrawerReport(rep);
      if (rep) {
        setReportForm({
          actualDate:          rep.actualDate?.slice(0, 10) || '',
          actualStartTime:     rep.actualStartTime || '',
          actualEndTime:       rep.actualEndTime || '',
          attendeesCount:      rep.attendeesCount,
          objective:           rep.objective,
          activitiesPerformed: rep.activitiesPerformed,
          observations:        rep.observations,
          agreements:          rep.agreements,
          incidents:           rep.incidents,
          pendingTasks:        rep.pendingTasks,
        });
        const evs = await apiRequest(`/reports/${rep.id}/evidences`, { method: 'GET' }, token);
        setDrawerEvidences(evs || []);
      } else {
        setReportForm(EMPTY_REPORT);
      }
    } catch (_) { /* no report yet */ }
  }

  function closeDrawer() {
    setDrawerAssembly(null);
    setDrawerReport(null);
    setDrawerEvidences([]);
    setReportForm(EMPTY_REPORT);
  }

  // ── Status
  async function changeStatus(assemblyId, status, extraPayload = {}) {
    if (status === 'canceled') {
      const reason = String(extraPayload.cancellationReason || '').trim();
      if (!reason) {
        notify('error', 'Debes capturar el motivo de cancelacion.');
        return;
      }
    }

    try {
      const updated = await apiRequest(`/assemblies/${assemblyId}`, {
        method: 'PATCH', body: JSON.stringify({ status, ...extraPayload }),
      }, token);
      setAssemblies(prev => prev.map(a => a.id === assemblyId ? updated : a));
      if (drawerAssembly?.id === assemblyId) setDrawerAssembly(updated);
      notify('success', `Estado: ${statusLabel(status)}.`);
    } catch (err) { notify('error', err.message); }
  }

  async function handleClickEvent(assembly) {
    await openDrawer(assembly);
  }

  // ── Create assembly
  function openCreateModal(dateStr) {
    setModalForm({
      ...EMPTY_ASSEMBLY,
      scheduledDate: dateStr || '',
    });
    setShowModal(true);
  }

  async function handleCreateAssembly(e) {
    e.preventDefault();
    if (modalForm.scheduledDate) {
      const scheduledYear = Number(String(modalForm.scheduledDate).slice(0, 4));
      const currentYear = new Date().getFullYear();
      if (Number.isFinite(scheduledYear) && scheduledYear < currentYear) {
        notify('error', 'La fecha programada no permite años pasados.');
        return;
      }
    }
    if (modalForm.startTime && modalForm.endTime && modalForm.endTime <= modalForm.startTime) {
      notify('error', 'La hora de término debe ser posterior a la hora de inicio.');
      return;
    }
    if (!String(modalForm.lat || '').trim() || !String(modalForm.lng || '').trim()) {
      notify('error', 'Debes marcar la ubicación en el mapa antes de programar la asamblea.');
      return;
    }
    setModalSaving(true);
    try {
      await apiRequest('/assemblies', {
        method: 'POST',
        body: JSON.stringify({ ...modalForm, scheduledDate: modalForm.scheduledDate }),
      }, token);

      setShowModal(false);
      await loadAssemblies();
      await loadOperationalMetrics(metricsDays);
      notify('success', 'Asamblea programada.');
    } catch (err) { notify('error', err.message); }
    finally { setModalSaving(false); }
  }

  // ── Report
  async function saveDraft(e) {
    e.preventDefault();
    if (!drawerAssembly) return;
    if (reportForm.actualStartTime && reportForm.actualEndTime && reportForm.actualEndTime <= reportForm.actualStartTime) {
      notify('error', 'La hora término debe ser posterior a la hora inicio.');
      return;
    }
    setReportSaving(true);
    try {
      const method = drawerReport ? 'PATCH' : 'POST';
      const rep = await apiRequest(`/assemblies/${drawerAssembly.id}/report`, {
        method, body: JSON.stringify({ ...reportForm, attendeesCount: Number(reportForm.attendeesCount) }),
      }, token);
      setDrawerReport(rep);
      notify('success', 'Informe guardado como borrador.');
    } catch (err) { notify('error', err.message); }
    finally { setReportSaving(false); }
  }

  async function submitReport() {
    if (!drawerAssembly) return;
    if (!drawerEvidences.length) {
      notify('error', 'Debes adjuntar al menos una evidencia fotográfica antes de enviar el informe definitivo.');
      setDrawerTab('evidences');
      return;
    }
    if (Number(reportForm.attendeesCount) === 0) {
      if (!window.confirm('El número de asistentes es 0. ¿Deseas continuar de todas formas?')) return;
    }
    try {
      const rep = await apiRequest(`/assemblies/${drawerAssembly.id}/report/submit`, { method: 'POST' }, token);
      setDrawerReport(rep);
      const evs = await apiRequest(`/reports/${rep.id}/evidences`, { method: 'GET' }, token);
      setDrawerEvidences(evs || []);
      await loadAssemblies();
      await loadOperationalMetrics(metricsDays);
      notify('success', 'Informe enviado y bloqueado.');
    } catch (err) { notify('error', err.message); }
  }

  // ── Evidences
  async function uploadEvidence(e) {
    e.preventDefault();
    if (!drawerReport?.id || !uploadFiles.length) { notify('error', 'Selecciona archivos.'); return; }
    setUploadSaving(true);
    const fd = new FormData();
    fd.append('fileType', uploadType);
    Array.from(uploadFiles).forEach(f => fd.append('files', f));
    try {
      await apiRequest(`/reports/${drawerReport.id}/evidences`, { method: 'POST', body: fd }, token);
      const evs = await apiRequest(`/reports/${drawerReport.id}/evidences`, { method: 'GET' }, token);
      setDrawerEvidences(evs || []);
      setUploadFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      await loadOperationalMetrics(metricsDays);
      notify('success', 'Evidencias adjuntadas.');
    } catch (err) { notify('error', err.message); }
    finally { setUploadSaving(false); }
  }

  async function deleteEvidence(evId) {
    try {
      await apiRequest(`/reports/${drawerReport.id}/evidences/${evId}`, { method: 'DELETE' }, token);
      setDrawerEvidences(prev => prev.filter(e => e.id !== evId));
      await loadOperationalMetrics(metricsDays);
      notify('success', 'Evidencia eliminada.');
    } catch (err) { notify('error', err.message); }
  }

  return {
    assemblies, loading,
    operationalMetrics, metricsLoading, metricsDays, setMetricsDays,
    myAssemblyHistory, myAssemblyHistoryLoading,
    newspaperAddresses,
    newspaperRequests,
    newspaperLoading,
    newspaperAddressSaving,
    newspaperRequestSaving,
    newspaperAddressForm,
    setNewspaperAddressForm,
    newspaperStates,
    newspaperMunicipalities,
    newspaperFederalDistricts,
    newspaperPostalCodes,
    newspaperColonias,
    newspaperCities,
    newspaperCatalogLoading,
    newspaperMunicipalitiesLoading,
    handleNewspaperStateChange,
    handleNewspaperMunicipalityChange,
    handleNewspaperPostalCodeChange,
    newspaperRequestForm,
    setNewspaperRequestForm,
    showModal, setShowModal, modalForm, setModalForm, modalSaving,
    drawerAssembly, drawerReport, drawerEvidences,
    drawerTab, setDrawerTab,
    reportForm, setReportForm, reportSaving,
    uploadType, setUploadType,
    uploadFiles, setUploadFiles, uploadSaving,
    fileRef,
    loadAssemblies, loadOperationalMetrics, refreshOperationalMetrics, loadNewspaperData, reset,
    openDrawer, closeDrawer,
    changeStatus, handleClickEvent,
    openCreateModal, handleCreateAssembly,
    saveDraft, submitReport,
    uploadEvidence, deleteEvidence,
    createNewspaperAddress,
    createNewspaperRequest,
  };
}
